/**
 * Step3_Specs.js
 * Physical specs (area, age, deed) + ALL amenities (HARD + SOFT).
 * Layout: specs → boolean amenities (chip row) → numerical/categorical (expandable cards)
 * Every amenity has required / اختیاری toggle stored in RequestAmenity.required
 */

import { useState, useEffect } from 'react';
import { api, apiRoutes } from '../../../api';
import { updateDraft, upsertAmenities } from '../services/propertyRequestService';
import { parseRangeDefinition } from '../components/HardAmenityInput';

/* ── Chip value selector for numerical / categorical ── */
function ChipValue({ amenity, value, onChange }) {
    const vt = amenity.value_type;

    if (vt === 'numerical') {
        const buckets = parseRangeDefinition(amenity.hcm_range_definition);
        if (!buckets.length) return null;
        const allKeys = buckets.map(b => b.key);
        const isAll = !value || !Array.isArray(value) || value.length === allKeys.length;
        const selSet = new Set((Array.isArray(value) ? value : []).map(String));
        const toggle = (key) => {
            const k = String(key);
            let next = selSet.has(k)
                ? [...selSet].filter(x => x !== k).map(Number)
                : [...selSet, k].map(Number);
            if (!next.length || next.length === allKeys.length) next = allKeys;
            onChange(next);
        };
        return (
            <div className="hai-chips" onClick={e => e.stopPropagation()}>
                <button type="button" className={`hai-chip hai-chip-any ${isAll ? 'hai-chip-on' : ''}`}
                    onClick={() => onChange(allKeys)}>مهم نیست</button>
                {buckets.map(b => (
                    <button key={b.key} type="button"
                        className={`hai-chip ${!isAll && selSet.has(String(b.key)) ? 'hai-chip-on' : ''}`}
                        onClick={() => toggle(b.key)}>{b.label}</button>
                ))}
            </div>
        );
    }

    if (vt === 'categorical') {
        const opts = amenity.categorical_options || {};
        const options = Object.entries(opts).map(([k, label]) => ({
            key: isNaN(Number(k)) ? k : Number(k), label,
        }));
        if (!options.length) return null;
        const allKeys = options.map(o => o.key);
        const isAll = !value || !Array.isArray(value) || value.length === allKeys.length;
        const selSet = new Set((Array.isArray(value) ? value : []).map(String));
        const toggle = (key) => {
            const k = String(key);
            let next = selSet.has(k)
                ? [...selSet].filter(x => x !== k).map(v => isNaN(Number(v)) ? v : Number(v))
                : [...selSet, k].map(v => isNaN(Number(v)) ? v : Number(v));
            if (!next.length || next.length === allKeys.length) next = allKeys;
            onChange(next);
        };
        return (
            <div className="hai-chips" onClick={e => e.stopPropagation()}>
                <button type="button" className={`hai-chip hai-chip-any ${isAll ? 'hai-chip-on' : ''}`}
                    onClick={() => onChange(allKeys)}>مهم نیست</button>
                {options.map(opt => (
                    <button key={opt.key} type="button"
                        className={`hai-chip ${!isAll && selSet.has(String(opt.key)) ? 'hai-chip-on' : ''}`}
                        onClick={() => toggle(opt.key)}>{opt.label}</button>
                ))}
            </div>
        );
    }

    return null;
}

export default function Step3_Specs({ requestId, onNext, onBack, initialData = {} }) {
    const [minArea, setMinArea] = useState(initialData.min_area_sqm || '');
    const [maxArea, setMaxArea] = useState(initialData.max_area_sqm || '');

    const [allAmenities, setAllAmenities] = useState([]);
    // { [amenityId]: { value, required } }
    const [amenityState, setAmenityState] = useState(() => {
        const init = {};
        [...(initialData.hard_amenities || []), ...(initialData.amenities || [])].forEach(a => {
            init[a.amenity] = { value: a.value ?? null, required: a.required ?? false };
        });
        return init;
    });
    const [loadingAmenities, setLoadingAmenities] = useState(true);
    const [search, setSearch] = useState('');
    const [saving, setSaving] = useState(false);
    const [error,  setError]  = useState('');

    useEffect(() => {
        // Fetch HARD then SOFT and merge
        Promise.all([
            api.get(apiRoutes.properties.amenities, { params: { hcm_type: 'HARD' } }).then(r => r.data?.results ?? r.data),
            api.get(apiRoutes.properties.amenities, { params: { hcm_type: 'SOFT' } }).then(r => r.data?.results ?? r.data),
        ])
            .then(([hard, soft]) => setAllAmenities([...hard, ...soft]))
            .catch(() => setError('خطا در بارگذاری امکانات.'))
            .finally(() => setLoadingAmenities(false));
    }, []);

    const filtered = allAmenities.filter(a =>
        (a.description || a.name).toLowerCase().includes(search.toLowerCase())
    );
    const booleans    = filtered.filter(a => a.value_type === 'boolean');
    const nonBooleans = filtered.filter(a => a.value_type !== 'boolean');

    const toggleBool = (amenity) => {
        setAmenityState(prev => {
            if (prev[amenity.id]) {
                const next = { ...prev };
                delete next[amenity.id];
                return next;
            }
            return { ...prev, [amenity.id]: { value: true, required: false } };
        });
    };

    const toggleCard = (amenity) => {
        const allKeys = amenity.hcm_range_definition
            ? Object.keys(amenity.hcm_range_definition).map(Number)
            : (amenity.categorical_options ? Object.keys(amenity.categorical_options).map(k => isNaN(Number(k)) ? k : Number(k)) : []);
        setAmenityState(prev => {
            if (prev[amenity.id]) {
                const next = { ...prev };
                delete next[amenity.id];
                return next;
            }
            return { ...prev, [amenity.id]: { value: allKeys, required: false } };
        });
    };

    const setVal = (id, val) =>
        setAmenityState(prev => ({ ...prev, [id]: { ...prev[id], value: val } }));

    const setReq = (id, req) =>
        setAmenityState(prev => ({ ...prev, [id]: { ...prev[id], required: req } }));

    const handleNext = async () => {
        if (minArea && maxArea && Number(minArea) > Number(maxArea)) {
            setError('حداقل متراژ نمی‌تواند بیشتر از حداکثر باشد.');
            return;
        }
        setError('');
        setSaving(true);
        try {
            const patch = {};
            if (minArea) patch.min_area_sqm = Number(minArea);
            if (maxArea) patch.max_area_sqm = Number(maxArea);
            if (Object.keys(patch).length) await updateDraft(requestId, patch);

            const items = Object.entries(amenityState).map(([id, s], idx) => ({
                amenity:  Number(id),
                rank:     idx + 1,
                required: s.required,
                value:    s.value ?? null,
            }));
            if (items.length) await upsertAmenities(requestId, items);

            onNext({ ...patch, amenities: items });
        } catch {
            setError('خطا در ذخیره. لطفاً دوباره تلاش کنید.');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div>
            <h2 className="prw-step-title">مشخصات و امکانات</h2>
            <p className="prw-step-desc">متراژ و امکانات مورد نظر را مشخص کنید.</p>

            {/* ── Specs row ── */}
            <div className="s3-row">
                <div className="prw-field">
                    <label className="prw-label" htmlFor="s3-min-area">حداقل متراژ (م²)</label>
                    <input id="s3-min-area" type="number" min="0" className="prw-input"
                        placeholder="مثال: ۶۰" value={minArea} onChange={e => setMinArea(e.target.value)} />
                </div>
                <div className="prw-field">
                    <label className="prw-label" htmlFor="s3-max-area">حداکثر متراژ (م²)</label>
                    <input id="s3-max-area" type="number" min="0" className="prw-input"
                        placeholder="مثال: ۱۵۰" value={maxArea} onChange={e => setMaxArea(e.target.value)} />
                </div>
            </div>

            {/* ── Amenities ── */}
            {!loadingAmenities && allAmenities.length > 0 && (
                <div className="s3-amenities-section">
                    <h3 className="prw-section-title">امکانات</h3>
                    <input className="prw-input" style={{ marginBottom: 14 }}
                        placeholder="جستجوی امکانات..." value={search}
                        onChange={e => setSearch(e.target.value)} />

                    {/* Boolean chips */}
                    {booleans.length > 0 && (
                        <div className="s3-bool-section">
                            <p className="s4-section-label">داشتن / نداشتن</p>
                            <div className="s3-bool-grid">
                                {booleans.map(a => {
                                    const isOn = !!amenityState[a.id];
                                    const isReq = amenityState[a.id]?.required ?? false;
                                    return (
                                        <div key={a.id} className={`s3-bool-card ${isOn ? 's3-bool-card-on' : ''}`}>
                                            <button type="button" className="s3-bool-card-toggle"
                                                onClick={() => toggleBool(a)}>
                                                <span className="s3-bool-knob" />
                                                <span className="s3-bool-card-name">{a.description || a.name}</span>
                                            </button>
                                            {isOn && (
                                                <button type="button"
                                                    className={`ar-req-btn ${isReq ? 'ar-req-on' : ''}`}
                                                    onClick={() => setReq(a.id, !isReq)}>
                                                    {isReq ? 'الزامی' : 'اختیاری'}
                                                </button>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {/* Numerical / categorical cards */}
                    {nonBooleans.length > 0 && (
                        <div className="s3-card-section">
                            <p className="s4-section-label">گزینه‌ای</p>
                            <ul className="s4-amenity-list">
                                {nonBooleans.map(a => {
                                    const isSelected = !!amenityState[a.id];
                                    const sel = amenityState[a.id] || {};
                                    return (
                                        <li key={a.id} className={`s4-amenity-item ${isSelected ? 's4-amenity-on' : ''}`}>
                                            <div className="s4-amenity-header" onClick={() => toggleCard(a)}>
                                                <span className="ar-check">{isSelected ? '✓' : ''}</span>
                                                <span className="s4-amenity-name">{a.description || a.name}</span>
                                                {isSelected && (
                                                    <button type="button"
                                                        className={`ar-req-btn ${sel.required ? 'ar-req-on' : ''}`}
                                                        onClick={e => { e.stopPropagation(); setReq(a.id, !sel.required); }}>
                                                        {sel.required ? 'الزامی' : 'اختیاری'}
                                                    </button>
                                                )}
                                            </div>
                                            {isSelected && (
                                                <div className="s4-amenity-value">
                                                    <ChipValue amenity={a} value={sel.value}
                                                        onChange={val => setVal(a.id, val)} />
                                                </div>
                                            )}
                                        </li>
                                    );
                                })}
                            </ul>
                        </div>
                    )}
                </div>
            )}
            {loadingAmenities && <p className="prw-step-desc">در حال بارگذاری امکانات...</p>}

            {error && <p className="prw-error">{error}</p>}

            <div className="prw-nav">
                <button className="prw-btn prw-btn-secondary" onClick={onBack}>→ مرحله قبل</button>
                <button className="prw-btn prw-btn-primary" onClick={handleNext} disabled={saving}>
                    {saving ? 'در حال ذخیره...' : 'مرحله بعد ←'}
                </button>
            </div>
        </div>
    );
}
